import './agenda-signals.css';

import type {
  CapacityResourceVM,
  CapacitySignalsVM,
  FillRateTone,
  WaitlistSignalsVM,
} from './agenda-signals-adapter';
import type React from 'react';

export interface AgendaSignalsSectionProps {
  /** Sinais da fila; null/undefined quando o tenant não tem `scheduling-waitlist`. */
  waitlist?: WaitlistSignalsVM | null;
  /** Sinais de capacidade; null/undefined quando não tem `scheduling-native`. */
  capacity?: CapacitySignalsVM | null;
  /** Salto para a agenda (onde moram as ações de fila/capacidade). */
  onOpenAgenda?: () => void;
}

const TONE_CLASS: Record<FillRateTone, string> = {
  good: 'agsig-tone--good',
  warn: 'agsig-tone--warn',
  bad: 'agsig-tone--bad',
  none: 'agsig-tone--none',
};
const BAR_CLASS: Record<FillRateTone, string> = {
  good: 'agsig-barfill--good',
  warn: 'agsig-barfill--warn',
  bad: 'agsig-barfill--bad',
  none: 'agsig-barfill--warn',
};
const TONE_WORD: Record<FillRateTone, string> = {
  good: 'saudável',
  warn: 'atenção',
  bad: 'crítico',
  none: 'sem dados ainda',
};

/**
 * Sinais de fila/capacidade da agenda nativa no painel do Supervisor (Frente B, Sessão 5).
 * Apresentacional puro: recebe VMs já adaptadas (a garantia mora no backend). Cada card só
 * aparece quando o tenant tem a cap correspondente (gate do FETCH na página).
 */
export function AgendaSignalsSection({
  waitlist,
  capacity,
  onOpenAgenda,
}: AgendaSignalsSectionProps): React.JSX.Element | null {
  const hasWaitlist = waitlist !== null && waitlist !== undefined;
  const hasCapacity = capacity !== null && capacity !== undefined;
  if (!hasWaitlist && !hasCapacity) return null;

  return (
    <section className="tela1-col__section">
      <div className="tela1-col__title">Agenda · fila e capacidade</div>

      {hasWaitlist && <WaitlistCard vm={waitlist} />}
      {hasCapacity && <CapacityCard vm={capacity} onOpenAgenda={onOpenAgenda} showLink={!hasWaitlist} />}
      {hasWaitlist && (
        <button type="button" className="agsig-link" onClick={onOpenAgenda}>
          Abrir agenda →
        </button>
      )}
    </section>
  );
}

function WaitlistCard({ vm }: { vm: WaitlistSignalsVM }): React.JSX.Element {
  const fillPct = vm.fillRate.ratePct;
  return (
    <div className="agsig-card">
      <div className="agsig-head">
        <span className="agsig-title">Fila de espera</span>
        <span className="agsig-meta">fila viva</span>
      </div>
      <div className="agsig-stats">
        <div className="agsig-stat">
          <span className="agsig-stat__num">{vm.waiting}</span>
          <span className="agsig-stat__lbl">na fila</span>
        </div>
        <div className={'agsig-stat' + (vm.pendingApproval > 0 ? ' agsig-stat--action' : '')}>
          <span className="agsig-stat__num">{vm.pendingApproval}</span>
          <span className="agsig-stat__lbl">aguardando liberação</span>
        </div>
      </div>
      {vm.tiers.length > 0 && (
        <div className="agsig-chips">
          {vm.tiers.map((t) => (
            <span
              key={t.label}
              className={'agsig-chip' + (t.urgent ? ' agsig-chip--urgent' : '')}
            >
              <b>{t.count}</b> {t.label}
            </span>
          ))}
        </div>
      )}
      {vm.topServices.length > 0 && (
        <div className="agsig-svc-list">
          {vm.topServices.map((s) => (
            <div key={s.serviceOfferingId} className="agsig-svc">
              <span>{s.name}</span>
              <span className="agsig-svc__count">{s.count}</span>
            </div>
          ))}
        </div>
      )}
      <div className="agsig-fill">
        <div className="agsig-fill__head">
          <span className="agsig-fill__lbl">
            Preenchimento pela fila · {vm.fillRate.windowDays} dias
          </span>
          <span className={'agsig-fill__pct ' + TONE_CLASS[vm.fillRate.tone]}>
            {fillPct === null ? '—' : `${fillPct}%`}
          </span>
        </div>
        <div className="agsig-bar">
          <div
            className={'agsig-bar__fill ' + BAR_CLASS[vm.fillRate.tone]}
            style={{ width: `${fillPct ?? 0}%` }}
          />
        </div>
        <div className="agsig-fill__sub">
          {fillPct === null
            ? 'Nenhuma inscrição resolvida na janela.'
            : `${vm.fillRate.booked} de ${vm.fillRate.resolved} resolvidos viraram agendamento · ${TONE_WORD[vm.fillRate.tone]}`}
        </div>
      </div>
    </div>
  );
}

function CapacityCard({
  vm,
  onOpenAgenda,
  showLink,
}: {
  vm: CapacitySignalsVM;
  onOpenAgenda?: (() => void) | undefined;
  showLink: boolean;
}): React.JSX.Element {
  return (
    <div className="agsig-card">
      <div className="agsig-head">
        <span className="agsig-title">Capacidade</span>
        <span className="agsig-meta">próximos {vm.horizonDays} dias</span>
      </div>
      {vm.nearLimit.length > 0 ? (
        <div className="agsig-res-list">
          {vm.nearLimit.map((r) => (
            <ResourceRow key={r.resourceId} r={r} />
          ))}
        </div>
      ) : (
        <div className="agsig-note">
          Nenhum recurso perto do limite — grupos (por capacidade) e exclusivos (por ocupação da
          grade) estão folgados no horizonte.
        </div>
      )}
      {vm.nearLimit.length === 0 && vm.active.length > 0 && (
        <>
          <div className="agsig-ctx-title">Mais movimentados</div>
          {vm.active.slice(0, 3).map((r) => (
            <div key={r.resourceId} className="agsig-svc">
              <span>
                {r.name}
                <span className="agsig-res__type">{r.typeLabel}</span>
              </span>
              <span className="agsig-svc__count">
                {r.isGroup ? `${r.peakConcurrent}/${r.capacity}` : `${r.displayUtilizationPct}%`}
              </span>
            </div>
          ))}
        </>
      )}
      {showLink && (
        <button type="button" className="agsig-link" onClick={onOpenAgenda}>
          Abrir agenda →
        </button>
      )}
    </div>
  );
}

function ResourceRow({ r }: { r: CapacityResourceVM }): React.JSX.Element {
  // Grupo: "cheio" = pico atinge a capacidade. Exclusivo (C1): "cheio" = grade 100% ocupada.
  const full = r.isGroup ? r.peakConcurrent >= r.capacity : r.displayUtilizationPct >= 100;
  return (
    <div className="agsig-res">
      <div className="agsig-res__head">
        <span>
          <span className="agsig-res__name">{r.name}</span>
          <span className="agsig-res__type">{r.typeLabel}</span>
        </span>
        <span className={'agsig-pill ' + (full ? 'agsig-pill--full' : 'agsig-pill--near')}>
          {full ? 'Lotado' : 'Perto do limite'}
        </span>
      </div>
      <div className="agsig-res__head">
        <div className="agsig-bar" style={{ flex: 1, marginRight: 10 }}>
          <div
            className={'agsig-bar__fill ' + (full ? 'agsig-barfill--bad' : 'agsig-barfill--warn')}
            style={{ width: `${Math.min(r.displayUtilizationPct, 100)}%` }}
          />
        </div>
        {/* Grupo mede capacidade (pico/cap); exclusivo mede TEMPO (% da grade). */}
        <span className="agsig-res__ratio">
          {r.isGroup ? `${r.peakConcurrent}/${r.capacity}` : `${r.displayUtilizationPct}% ${r.metricLabel}`}
        </span>
      </div>
    </div>
  );
}
